import React, { useState } from 'react';
import { X, Maximize2, Minimize2 } from 'lucide-react';

const LegalModal = ({ isOpen, onClose, type }) => {
  const [isMaximized, setIsMaximized] = useState(false);

  if (!isOpen) return null;

  const isTerms = type === 'terms';

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={onClose}
    >
      <div 
        className={`bg-white rounded-xl shadow-2xl flex flex-col transition-all duration-300 ${
          isMaximized 
            ? 'w-full h-full m-0 rounded-none' 
            : 'w-[90%] max-w-2xl h-[70vh] m-4'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-grey-200">
          <h2 className="text-lg font-semibold text-grey-900">
            {isTerms ? 'Terms of Service' : 'Privacy Policy'}
          </h2>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setIsMaximized(!isMaximized)}
              className="p-2 rounded-lg text-grey-500 hover:text-grey-700 hover:bg-grey-100 transition-colors"
              aria-label={isMaximized ? 'Minimize' : 'Maximize'}
            >
              {isMaximized ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
            </button>
            <button
              onClick={onClose}
              className="p-2 rounded-lg text-grey-500 hover:text-grey-700 hover:bg-grey-100 transition-colors"
              aria-label="Close"
            >
              <X size={18} />
            </button>
          </div>
        </div>
        
        <div className="flex-1 overflow-y-auto p-6">
          {isTerms ? <TermsContent /> : <PrivacyContent />}
        </div>

        <div className="p-4 border-t border-grey-200 bg-grey-50">
          <p className="text-xs text-grey-500 text-center">
            Last Updated: November 27, 2025 | Pebble The App, Inc.
          </p>
        </div>
      </div>
    </div>
  );
};

const TermsContent = () => (
  <div className="prose prose-sm max-w-none text-grey-700 space-y-6">
    <section>
      <h3 className="text-lg font-semibold text-grey-900 mb-3">1. Introduction and Acceptance</h3>
      <p className="text-sm leading-relaxed">
        Welcome to Pebble. These Terms of Use ("Terms") constitute a legally binding agreement between you and Pebble The App, Inc., 
        a Delaware corporation ("Pebble," "we," "us," or "our") governing your access to and use of the Pebble platform, website, mobile applications, 
        and all related services (collectively, the "Service").
      </p>
      <p className="text-sm leading-relaxed mt-2">
        <strong>BY ACCESSING OR USING THE SERVICE, YOU AGREE TO BE BOUND BY THESE TERMS.</strong> If you do not agree to these 
        Terms, you may not access or use the Service.
      </p>
    </section>

    <section>
      <h3 className="text-lg font-semibold text-grey-900 mb-3">2. Definitions</h3>
      <ul className="list-disc list-inside space-y-1 text-sm">
        <li><strong>"Event Organizer" or "EO"</strong>: A user who creates, manages, and hosts events through the Service.</li>
        <li><strong>"Marketplace Vendor," "Marketplace Seller," or "MPS"</strong>: A user who registers to participate as a vendor at events.</li>
        <li><strong>"Platform Fee"</strong>: The percentage fee (currently 15%) retained by Pebble from vendor registration payments.</li>
        <li><strong>"User"</strong>: Any individual or entity accessing or using the Service.</li>
      </ul>
    </section>

    <section>
      <h3 className="text-lg font-semibold text-grey-900 mb-3">3. Platform Role</h3>
      <p className="text-sm leading-relaxed">
        Pebble is a technology platform and marketplace intermediary. We provide the technology infrastructure 
        that enables Event Organizers to create and manage events, and Marketplace Vendors to discover and register for those events.
        We are not a party to transactions between users.
      </p>
    </section>

    <section>
      <h3 className="text-lg font-semibold text-grey-900 mb-3">4. Account Security</h3>
      <p className="text-sm leading-relaxed">
        You must be at least 18 years old to create an account. You agree to maintain the security of your login credentials 
        and accept responsibility for all activities under your account.
      </p>
    </section>

    <section>
      <h3 className="text-lg font-semibold text-grey-900 mb-3">5. Payment Terms</h3>
      <p className="text-sm leading-relaxed">
        Pebble uses Stripe Connect for payment processing. A 15% platform fee is retained from vendor registration payments, 
        with 85% transferred to the Event Organizer. All payments are non-refundable unless otherwise stated.
      </p>
    </section>

    <section>
      <h3 className="text-lg font-semibold text-grey-900 mb-3">6. Prohibited Conduct</h3>
      <ul className="list-disc list-inside space-y-1 text-sm">
        <li>Violating any applicable laws or regulations</li>
        <li>Posting false, misleading, or fraudulent content</li>
        <li>Attempting to circumvent platform fees or security measures</li>
        <li>Harassing or threatening other users</li>
      </ul>
    </section>

    <section>
      <h3 className="text-lg font-semibold text-grey-900 mb-3">7. Limitation of Liability</h3>
      <p className="text-sm leading-relaxed">
        TO THE MAXIMUM EXTENT PERMITTED BY LAW, PEBBLE SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, 
        CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING FROM YOUR USE OF THE SERVICE.
      </p>
    </section>

    <section>
      <h3 className="text-lg font-semibold text-grey-900 mb-3">8. Dispute Resolution</h3>
      <p className="text-sm leading-relaxed">
        Any disputes arising from these Terms shall be resolved through binding arbitration in accordance with Delaware law. 
        You agree to waive any right to participate in a class action lawsuit.
      </p>
    </section>

    <section>
      <h3 className="text-lg font-semibold text-grey-900 mb-3">9. Contact Us</h3>
      <p className="text-sm leading-relaxed">
        For questions about these Terms, contact us at: legal@pebbletheapp.com
      </p>
    </section>
  </div>
);

const PrivacyContent = () => (
  <div className="prose prose-sm max-w-none text-grey-700 space-y-6">
    <section>
      <h3 className="text-lg font-semibold text-grey-900 mb-3">1. Introduction</h3>
      <p className="text-sm leading-relaxed">
        Pebble The App, Inc. ("Pebble," "we," "us," or "our"), a Delaware corporation, is committed to protecting your privacy. 
        This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our platform.
      </p>
      <p className="text-sm leading-relaxed mt-2">
        By accessing or using the Service, you consent to the practices described in this Privacy Policy.
      </p>
    </section>

    <section>
      <h3 className="text-lg font-semibold text-grey-900 mb-3">2. Information We Collect</h3>
      <p className="text-sm font-medium mb-2">Information You Provide:</p>
      <ul className="list-disc list-inside space-y-1 text-sm">
        <li>Account Information: Name, email, phone number, profile photo</li>
        <li>Business Profile: Business name, description, category, address</li>
        <li>Payment Information: Billing details (processed securely by Stripe)</li>
        <li>Documents: Contracts, permits, insurance certificates</li>
      </ul>
      <p className="text-sm font-medium mt-3 mb-2">Information Collected Automatically:</p>
      <ul className="list-disc list-inside space-y-1 text-sm">
        <li>Device and browser information</li>
        <li>Usage patterns and analytics</li>
        <li>IP address and general location</li>
      </ul>
    </section>

    <section>
      <h3 className="text-lg font-semibold text-grey-900 mb-3">3. How We Use Your Information</h3>
      <ul className="list-disc list-inside space-y-1 text-sm">
        <li>To provide and maintain the Service</li>
        <li>To process transactions and send related information</li>
        <li>To send administrative and promotional communications</li>
        <li>To detect and prevent fraud and abuse</li>
        <li>To comply with legal obligations</li>
      </ul>
    </section>

    <section>
      <h3 className="text-lg font-semibold text-grey-900 mb-3">4. Information Sharing</h3>
      <p className="text-sm leading-relaxed">
        We may share your information with Event Organizers (when you apply to events), service providers (Stripe, Firebase, SendGrid), 
        and as required by law. We do not sell your personal information.
      </p>
    </section>

    <section>
      <h3 className="text-lg font-semibold text-grey-900 mb-3">5. Data Security</h3>
      <p className="text-sm leading-relaxed">
        We implement industry-standard security measures including encryption, secure servers, and access controls. 
        However, no method of transmission over the Internet is 100% secure.
      </p>
    </section>

    <section>
      <h3 className="text-lg font-semibold text-grey-900 mb-3">6. Your Rights</h3>
      <p className="text-sm leading-relaxed">
        Depending on your location, you may have rights to access, correct, delete, or port your data. 
        This includes rights under GDPR (EU), CCPA (California), and DPDPA (Delaware).
      </p>
    </section>

    <section>
      <h3 className="text-lg font-semibold text-grey-900 mb-3">7. Cookies</h3>
      <p className="text-sm leading-relaxed">
        We use cookies and similar technologies for authentication, preferences, and analytics. 
        You can control cookies through your browser settings.
      </p>
    </section>

    <section>
      <h3 className="text-lg font-semibold text-grey-900 mb-3">8. Data Retention</h3>
      <p className="text-sm leading-relaxed">
        We retain your information for as long as your account is active or as needed to provide services. 
        We may retain certain information as required by law or for legitimate business purposes.
      </p>
    </section>

    <section>
      <h3 className="text-lg font-semibold text-grey-900 mb-3">9. Contact Us</h3>
      <p className="text-sm leading-relaxed">
        For privacy-related questions, contact us at: privacy@pebbletheapp.com
      </p>
    </section>
  </div>
);

export default LegalModal;
